import { animate, style, transition, trigger } from '@angular/animations';
import { NgClass, NgStyle } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AppService, Page } from '@core/services/app.service';
import { TranslateModule } from '@ngx-translate/core';
import { ClickOutsideDirective } from '@shared/directives/clickoutSide.directive';
import { ImageLoaderDirective } from '@shared/directives/image-loader.directive';
import { LocalizedDatePipe } from '@shared/pipes/localized-date.pipe';
import { PreventParentScrollDirective } from '@shared/directives/prevent-scrolling.directive';
import { SharedService } from '@core/services/shared.service';
import { GlobalHandlerService } from '@core/services/global-handler.service';
import { SellableProduct } from '@shared/classes/sellable-product';
import { customEcommerce } from 'src/assets/custom-ecommerce/custom';
import { productTypes } from '@shared/heplers/const-data';
import { handleEmpty } from '@shared/heplers/helpers';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-search-bar',
  templateUrl: './search-bar.component.html',
  styleUrl: './search-bar.component.scss',
  animations: [
    trigger('slideDownAnimation', [
      transition('up => down', [
        style({ height: 0, opacity: 0 }),
        animate('300ms ease-out', style({ height: '*', opacity: 1 }))
      ]),
      transition('down => up', [
        style({ height: '*', opacity: 1 }),
        animate('150ms ease-in', style({ height: 0, opacity: 0 }))
      ])
    ])
  ], 
  imports: [
    NgClass,
    NgStyle,
    ClickOutsideDirective,
    FormsModule,
    ImageLoaderDirective,
    TranslateModule,
    LocalizedDatePipe,
    PreventParentScrollDirective
  ]
})
export class SearchBarComponent implements OnInit {

  @Input() isCustom: boolean;
  @Input() isHeaderBreakpoint: boolean;

  searchBarPage = new Page();
  isSearchActive: boolean = false;
  searchInputValue: string = "";
  lastSearchInputValue: string = "";
  loadedAllSearchItems: boolean = false;
  isLoadingSearchItems: boolean = false;
  isLoadingNextPage: boolean = false; 
  isClosingSearch: boolean = false;
  searchAnimationState: 'up' | 'down' = 'up';
  showNoRecords: boolean = false;
  products: SellableProduct[] = [];
  customConfig: any;
  showcart : boolean = false;
  timeoutSearch: NodeJS.Timeout = null;
    defaultBurgerColor : string = '#FFFFFF'

  @ViewChild('searchInput', { static: false }) searchInput: ElementRef;

  constructor(
    private appService: AppService,
    private sharedService: SharedService,
    private globalHandlerService: GlobalHandlerService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private cd: ChangeDetectorRef
  ) {
    this.customConfig = customEcommerce[this.sharedService.getCustomDomain()]
  }

  ngOnInit(): void {}

  onSearchClick() {
    if (environment.allowPixelTrack) {
      this.trackSearch();
    }
    if (this.searchInput) {
      this.searchInput.nativeElement.focus();
    }
  }

  onSearchInput() {
    clearTimeout(this.timeoutSearch);
    if (this.isClosingSearch) {
      return;
    }
    this.timeoutSearch = setTimeout(() => {
      this.searchBarPage.page = 0;
      this.isSearchActive = true;
      if (handleEmpty(this.searchInputValue)) {
        const newSearchValue = this.searchInputValue.trim();
        if (this.lastSearchInputValue === newSearchValue) {
          return;
        } else {
          this.lastSearchInputValue = newSearchValue;
        }
        if (this.products.length > 0 || this.showNoRecords) {
          this.searchAnimationState = 'up';
          setTimeout(() => {
            this.products = [];
            this.showNoRecords = false;
            this.getSellableProducts(true);
          }, 150);
        } else {
          this.getSellableProducts(true);
        }
      } else if (this.products.length > 0 || this.showNoRecords) {
        this.closeSearchResultsDropdown();
      }
    }, 500);
  }

  onLoadNextPage() {
    if (!this.isLoadingSearchItems && !this.loadedAllSearchItems) {
      this.searchBarPage.page++;
      this.getSellableProducts(false);
    }
  }

  getSellableProducts(resetResults: boolean) {
    this.isLoadingSearchItems = true;
    this.showNoRecords = false;
    let productParams = {
      q: this.searchInputValue,
      sort: 'startDateTime,ASC',
      page: this.searchBarPage.page,
      size: this.searchBarPage.size
    }
    if (this.isCustom) {
      productParams['aid'] = this.sharedService.getCustomAreaIds()
    }
    this.appService.getAll("planning/public/sellableproducts", productParams).subscribe((response) => {
      if (!this.isSearchActive) {
        return;
      }
      if (response._embedded) {
        if(response._embedded.sellableProductTableDToes.length > 0){
          this.products = resetResults
                          ? response._embedded.sellableProductTableDToes 
                          : this.products.concat(response._embedded.sellableProductTableDToes);
          this.searchAnimationState = 'down';
          this.showNoRecords = false;
        } else if (this.products.length === 0) {
          this.showNoRecords = true;
          this.searchAnimationState = 'down';
        }
        const totalItems: number = response.page ? response.page.totalElements : this.products.length;
        this.loadedAllSearchItems = this.products.length < totalItems ? false : true;
      } else {
        this.products = [];
        this.loadedAllSearchItems = true;
        this.searchAnimationState = 'down';
        this.showNoRecords = true;
      }
      this.isLoadingSearchItems = false
    }, (error) => {
      this.closeSearchResultsDropdown();
      this.globalHandlerService.showError(error);
    });
  }

  showProductDetails(product: SellableProduct) {
    let productType = product.objectType === productTypes.SUB? productTypes.SUBSCRIPTION : productTypes.EVENTO;
    const mvParam = this.activatedRoute.snapshot.queryParams['mv'];
    this.router.navigate([`./${productType}/` + product.resourceId], mvParam ? { queryParams: { mv: mvParam } } : {});
    this.closeSearchResultsDropdown();
  }

  closeSearchResultsDropdown() {
    this.isSearchActive = false;
    this.isClosingSearch = true;
    this.searchAnimationState = 'up';

    setTimeout(() => {
      this.resetSearch();
      this.isClosingSearch = false;
    }, 150);
  }

  resetSearch(){
    this.searchBarPage.page = 0;
    this.products = [];
    this.searchInputValue = "";
    this.lastSearchInputValue = "";
    this.loadedAllSearchItems = false;
    this.isLoadingSearchItems = false;
    this.showNoRecords = false;
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const clickedInside = target.closest('.search-container');
    if (!clickedInside && this.isSearchActive) {
      this.closeSearchResultsDropdown();
    }
  }

  trackSearch() {
    let pixels = this.sharedService.getPixelIds();
    if (pixels.length > 0) {
      this.sharedService.grantPixelTracking();
      pixels.forEach(pixel => {
        (window as any).fbq('trackSingle', pixel.toString(), 'Search');
      });
    }
  }
}
